@if (label()) {
    <label class="sh-editor-label text-shui-text-strong">
        {{ label() }}
        @if (required()) {
            <span class="text-shui-text-strong ml-0.5">*</span>
        }
    </label>
}
<div
    #editorShell
    class="sh-editor-shell"
    data-testid="global-editor-container"
    [class.sh-editor-shell-dark]="theme() === 'dark'"
    [class.sh-editor-shell-light]="theme() === 'light'"
    [class.sh-editor-shell-disabled]="isDisabled()"
>
    <ng-template #toolbarControls>
        <div class="sh-editor-toolbar-controls" data-testid="global-editor-toolbar-container">
            <div class="sh-editor-toolbar-group">
                <sh-icon
                    icon="undo"
                    size="s"
                    button
                    data-testid="global-editor-button-undo"
                    [disabled]="isDisabled() || !canUndo()"
                    (click)="executeToolbarCommand($event, 'undo')"
                    [title]="'common.editor.undo' | translate"
                    aria-label="Undo"
                ></sh-icon>
                <sh-icon
                    icon="redo"
                    size="s"
                    button
                    data-testid="global-editor-button-redo"
                    [disabled]="isDisabled() || !canRedo()"
                    (click)="executeToolbarCommand($event, 'redo')"
                    [title]="'common.editor.redo' | translate"
                    aria-label="Redo"
                ></sh-icon>
            </div>

            <div class="sh-editor-toolbar-divider" aria-hidden="true"></div>

            <div class="sh-editor-toolbar-group">
                <sh-icon
                    icon="bold"
                    size="s"
                    button
                    toggle
                    class="scale-[0.9]"
                    data-testid="global-editor-button-bold"
                    [attr.active]="activeFormats().bold ? true : null"
                    [disabled]="isDisabled()"
                    (click)="executeToolbarCommand($event, 'bold')"
                    [title]="'common.editor.bold' | translate"
                    aria-label="Bold"
                ></sh-icon>
                <sh-icon
                    [icon]="icons().italic"
                    size="s"
                    button
                    toggle
                    class="scale-[0.9]"
                    data-testid="global-editor-button-italic"
                    [attr.active]="activeFormats().italic ? true : null"
                    [disabled]="isDisabled()"
                    (click)="executeToolbarCommand($event, 'italic')"
                    [title]="'common.editor.italic' | translate"
                    aria-label="Italic"
                ></sh-icon>
                <sh-icon
                    [icon]="icons().underline"
                    size="s"
                    button
                    toggle
                    class="scale-[0.9]"
                    data-testid="global-editor-button-underline"
                    [attr.active]="activeFormats().underline ? true : null"
                    [disabled]="isDisabled()"
                    (click)="executeToolbarCommand($event, 'underline')"
                    [title]="'common.editor.underline' | translate"
                    aria-label="Underline"
                ></sh-icon>
                <sh-icon
                    [icon]="icons().strike"
                    size="s"
                    button
                    toggle
                    class="scale-[0.9]"
                    data-testid="global-editor-button-strike"
                    [attr.active]="activeFormats().strike ? true : null"
                    [disabled]="isDisabled()"
                    (click)="executeToolbarCommand($event, 'strike')"
                    [title]="'common.editor.strikethrough' | translate"
                    aria-label="Strikethrough"
                ></sh-icon>
            </div>

            <div class="sh-editor-toolbar-divider" aria-hidden="true"></div>

            <div class="sh-editor-toolbar-group">
                <sh-icon
                    icon="list-bulleted"
                    size="s"
                    button
                    toggle
                    class="scale-[0.8]"
                    data-testid="global-editor-button-bullet-list"
                    [attr.active]="activeFormats().bulletList ? true : null"
                    [disabled]="isDisabled()"
                    (click)="executeToolbarCommand($event, 'bulletList')"
                    [title]="'common.editor.unordered-list' | translate"
                    aria-label="Bullet list"
                ></sh-icon>
                <sh-icon
                    icon="list-viewmode"
                    size="s"
                    button
                    toggle
                    class="scale-[0.8]"
                    data-testid="global-editor-button-ordered-list"
                    [attr.active]="activeFormats().orderedList ? true : null"
                    [disabled]="isDisabled()"
                    (click)="executeToolbarCommand($event, 'orderedList')"
                    [title]="'common.editor.ordered-list' | translate"
                    aria-label="Numbered list"
                ></sh-icon>
            </div>

            <div class="sh-editor-toolbar-divider" aria-hidden="true"></div>

            <div class="sh-editor-toolbar-group">
                <sh-icon
                    icon="sync-on"
                    size="s"
                    button
                    toggle
                    class="scale-[0.9]"
                    data-testid="global-editor-button-insert-link"
                    data-sh-editor-link-trigger="true"
                    [attr.active]="activeFormats().link ? true : null"
                    [disabled]="isDisabled() || !canInsertLink()"
                    (click)="openLinkMenu($event)"
                    [title]="'common.editor.insert-link' | translate"
                    aria-label="Insert link"
                ></sh-icon>

                <sh-icon
                    icon="eraser"
                    size="s"
                    button
                    class="scale-[0.9]"
                    data-testid="global-editor-button-clear-formatting"
                    [disabled]="isDisabled()"
                    (click)="executeToolbarCommand($event, 'clearFormatting')"
                    [title]="'common.editor.clear-formatting' | translate"
                    aria-label="Clear formatting"
                ></sh-icon>

                @if (showAttachmentButton() && allowAttachments()) {
                    <input
                        #attachmentInput
                        type="file"
                        class="hidden"
                        data-testid="global-editor-input-attachment"
                        [multiple]="true"
                        [accept]="acceptedFileTypes()"
                        (change)="handleAttachmentInput($event)"
                    />
                    <sh-icon
                        icon="attachment"
                        size="s"
                        button
                        class="scale-[0.9]"
                        data-testid="global-editor-button-attach-files"
                        [disabled]="isDisabled()"
                        (click)="attachmentInput.click()"
                        [title]="'common.editor.attach-files' | translate"
                        aria-label="Attach files"
                    ></sh-icon>
                }
            </div>

            <ng-content select="[toolbarStart]"></ng-content>
        </div>
    </ng-template>

    @if (showToolbar() && !showBottomBar()) {
        <div class="sh-editor-toolbar">
            <ng-container [ngTemplateOutlet]="toolbarControls"></ng-container>

            <div class="sh-editor-toolbar-extras">
                <ng-content select="[toolbarEnd]"></ng-content>
            </div>
        </div>
    }

    <div class="sh-editor-surface" (click)="handleSpellcheckWordClick($event)">
        <div
            tiptap
            [editor]="editor"
            [style.min-height]="minHeight()"
            [style.max-height]="maxHeight()"
            [style.overflow-y]="'auto'"
            class="sh-editor-host"
            (scroll)="handleEditorSurfaceScroll()"
        ></div>
    </div>

    <div
        [class.sh-editor-toolbar]="showToolbar() && showBottomBar()"
        [class.sh-editor-toolbar-bottom]="showToolbar() && showBottomBar()"
        [class.sh-editor-footer]="!showBottomBar() || !showToolbar()"
    >
        @if (showToolbar() && showBottomBar()) {
            <ng-container [ngTemplateOutlet]="toolbarControls"></ng-container>

            <div class="sh-editor-toolbar-extras">
                <ng-content select="[toolbarEnd]"></ng-content>
            </div>
        } @else {
            <ng-content select="[bottomActions]"></ng-content>
        }
    </div>

    @if (linkMenu(); as menu) {
        <sh-editor-link-menu
            [menu]="menu"
            (closed)="closeLinkMenuFromAction()"
            (linkApplied)="applyLinkFromMenu($event)"
            (linkRemoved)="removeLinkFromMenu($event)"
        ></sh-editor-link-menu>
    }

    @if (spellcheckSuggestionMenu(); as menu) {
        <sh-editor-spellcheck-menu
            [menu]="menu"
            (suggestionSelected)="applySpellcheckSuggestion($event)"
        ></sh-editor-spellcheck-menu>
    }
</div>
